import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../lib/client/client";

/**
 * Hook to handle session timeout by inactivity
 * Disconnects the user after 30 minutes of inactivity
 */
export const useSessionTimeout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

  const resetTimeout = () => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Create new timeout
    timeoutRef.current = setTimeout(() => {
      // Disconnect user
      apiClient.logout();
      navigate("/login", {
        replace: true,
        state: { message: t("login.sessionExpired") },
      });
    }, INACTIVITY_TIMEOUT);
  };

  useEffect(() => {
    // Do not activate timeout if user is not authenticated
    if (!apiClient.isAuthenticated()) {
      return;
    }

    // Events that reset the timeout
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    // Initialize timeout
    resetTimeout();

    // Add event listeners
    events.forEach((event) => {
      document.addEventListener(event, resetTimeout);
    });

    // Clean up listeners and timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, resetTimeout);
      });
    };
  }, [navigate, t]);
};
